import { useState, useEffect } from "react";
import crudApi from "@serie3/common/api/crudApi";
import DocumentsEndpoints from "@serie3/mymediaplanner/Modules/Documents/endpoints";

/**
 * Loads a document's binary and reports its natural pixel size.
 *
 * The natural size matters beyond display: the overlay layout is resolved
 * against the image's own aspect ratio, and that is also the ratio it is
 * rendered at on publish. Editing a 9:16 image therefore edits the 9:16 layout.
 */
const useDocumentImage = (documentId, binaryEndpoint = DocumentsEndpoints.show) => {
  const [src, setSrc] = useState(null);
  const [size, setSize] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!documentId) {
      setSrc(null);
      setSize(null);
      return undefined;
    }

    let objectUrl = null;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await crudApi.getBinary(binaryEndpoint + documentId);
        if (cancelled) return;

        if (response.status !== 200) {
          setError("load-failed");
          setLoading(false);
          return;
        }

        const contentType = response.headers["content-type"] || "";
        if (!contentType.startsWith("image/")) {
          setError("not-an-image");
          setLoading(false);
          return;
        }

        objectUrl = window.URL.createObjectURL(response.data);
        const probe = new Image();
        probe.onload = () => {
          if (cancelled) return;
          setSize({ width: probe.naturalWidth, height: probe.naturalHeight });
          setSrc(objectUrl);
          setLoading(false);
        };
        probe.onerror = () => {
          if (cancelled) return;
          setError("decode-failed");
          setLoading(false);
        };
        probe.src = objectUrl;
      } catch (e) {
        if (!cancelled) {
          setError("load-failed");
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
      if (objectUrl) {
        window.URL.revokeObjectURL(objectUrl);
      }
    };
  }, [documentId, binaryEndpoint]);

  return { src, size, loading, error };
};

export default useDocumentImage;
